import { create } from 'zustand'
import type { StageJudgement, StageCompletion, DealStage, HealthCardResponse, ChangeAlert } from '@/types'
import { stageService, diagnosisService, changeService } from '@/services/dealService'

/**
 * 商机状态类型定义
 */
export interface Deal {
  id: string
  name: string
  customerName: string
  stage: string
  industry?: string
  amountEst?: number
  score: number
  confidence: number
  healthStatus: 'cold' | 'red' | 'yellow' | 'green'
  fatalGap?: string
  ownerId: string
  dealUrgencyDays?: number
  lastActionGradeUsed?: 'A' | 'B' | 'C'
  createdAt: string
  updatedAt: string
}

export interface Person {
  id: string
  dealId: string
  name: string
  title?: string
  roleTags: string[]
  attitude: 'support' | 'neutral' | 'oppose' | 'unknown'
  influence?: 'high' | 'medium' | 'low'
  isConfirmedEb: boolean
}

export interface Hypothesis {
  id: string
  dealId: string
  dimension: 'customer' | 'demand' | 'value' | 'barrier' | 'risk'
  category: string
  statement: string
  status: 'unknown' | 'plausible' | 'confirmed' | 'falsified'
  evidenceIds: string[]
  reasoning?: string
  isFatalGap: boolean
}

export interface Action {
  id: string
  hypothesisId: string
  type: 'question' | 'activity' | 'artifact' | 'touchpoint'
  content: string
  targetPersonId?: string
  priority: 'P0' | 'P1' | 'P2'
  status: 'proposed' | 'accepted' | 'done' | 'rejected'
  actionGrade: 'A' | 'B' | 'C'
  timeCostDays: number
  depthLevel: 'Full' | 'Quick' | 'Defense'
  idealScenario?: string
  fallbackActionId?: string
  expectedSignals: {
    strength: 'strong' | 'medium' | 'weak'
    description: string
  }[]
}

/**
 * 商机Store状态接口
 */
interface DealState {
  // 状态
  deals: Deal[]
  currentDeal: Deal | null
  persons: Person[]
  hypotheses: Hypothesis[]
  actions: Action[]
  isLoading: boolean
  error: string | null
  
  // T9: 阶段感知状态
  stageJudgement: StageJudgement | null
  stageCompletion: StageCompletion | null
  healthCard: HealthCardResponse | null
  isStageLoading: boolean
  
  // T11: 变更预警状态
  changeAlerts: ChangeAlert[]
  isChangeAlertsLoading: boolean

  // 动作
  setDeals: (deals: Deal[]) => void
  setCurrentDeal: (deal: Deal | null) => void
  setPersons: (persons: Person[]) => void
  setHypotheses: (hypotheses: Hypothesis[]) => void
  setActions: (actions: Action[]) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
  
  // T9: 阶段感知动作
  setStageJudgement: (judgement: StageJudgement | null) => void
  setStageCompletion: (completion: StageCompletion | null) => void
  setHealthCard: (healthCard: HealthCardResponse | null) => void
  fetchStageInfo: (dealId: string) => Promise<void>
  overrideStage: (dealId: string, stage: DealStage, reason?: string) => Promise<void>
  cancelStageOverride: (dealId: string) => Promise<void>
  
  // T11: 变更预警动作
  setChangeAlerts: (alerts: ChangeAlert[]) => void
  fetchChangeAlerts: (dealId: string) => Promise<void>
  dismissChangeAlert: (alertId: string, reason?: string) => Promise<void>
}

/**
 * 商机Store
 */
export const useDealStore = create<DealState>((set, _get) => ({
  // 初始状态
  deals: [],
  currentDeal: null,
  persons: [],
  hypotheses: [],
  actions: [],
  isLoading: false,
  error: null,
  
  // T9: 阶段感知初始状态
  stageJudgement: null,
  stageCompletion: null,
  healthCard: null,
  isStageLoading: false,
  
  // T11: 变更预警初始状态 (BR-T11-06)
  changeAlerts: [],
  isChangeAlertsLoading: false,

  // 动作
  setDeals: (deals) => set({ deals }),
  setCurrentDeal: (currentDeal) => set({ currentDeal }),
  setPersons: (persons) => set({ persons }),
  setHypotheses: (hypotheses) => set({ hypotheses }),
  setActions: (actions) => set({ actions }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
  
  // T9: 阶段感知动作
  setStageJudgement: (stageJudgement) => set({ stageJudgement }),
  setStageCompletion: (stageCompletion) => set({ stageCompletion }),
  setHealthCard: (healthCard) => set({ healthCard }),
  
  /**
   * 获取阶段信息（判定 + 完成度）
   * BR-T9-06: stageJudgement 状态初始 null
   */
  fetchStageInfo: async (dealId: string) => {
    set({ isStageLoading: true, error: null })
    try {
      const [judgement, completion] = await Promise.all([
        stageService.getStageJudgement(dealId),
        stageService.getStageCompletion(dealId),
      ])
      set({
        stageJudgement: judgement,
        stageCompletion: completion,
        isStageLoading: false,
      })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : '获取阶段信息失败',
        isStageLoading: false,
      })
    }
  },
  
  /**
   * 手动覆盖阶段
   * BR-T9-07: overrideStage 后自动刷新 stageInfo
   * BR-T9-08: overrideStage 后自动刷新 healthCard
   */
  overrideStage: async (dealId: string, stage: DealStage, reason?: string) => {
    set({ isStageLoading: true, error: null })
    try {
      const judgement = await stageService.overrideStage(dealId, { stage, reason })
      set({ stageJudgement: judgement })
      
      // BR-T9-07: 自动刷新完成度
      const completion = await stageService.getStageCompletion(dealId)
      set({ stageCompletion: completion })
      
      // BR-T9-08: 自动刷新体检卡
      const healthCard = await diagnosisService.getHealthCard(dealId)
      set({ healthCard, isStageLoading: false })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : '覆盖阶段失败',
        isStageLoading: false,
      })
    }
  },
  
  /**
   * 取消阶段覆盖
   */
  cancelStageOverride: async (dealId: string) => {
    set({ isStageLoading: true, error: null })
    try {
      const judgement = await stageService.cancelStageOverride(dealId)
      set({ stageJudgement: judgement })
      
      // 自动刷新完成度和体检卡
      const [completion, healthCard] = await Promise.all([
        stageService.getStageCompletion(dealId),
        diagnosisService.getHealthCard(dealId),
      ])
      set({
        stageCompletion: completion,
        healthCard,
        isStageLoading: false,
      })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : '取消覆盖失败',
        isStageLoading: false,
      })
    }
  },
  
  // ============================================
  // T11: 变更预警动作
  // ============================================
  
  setChangeAlerts: (changeAlerts) => set({ changeAlerts }),
  
  /**
   * 获取变更预警列表
   * BR-T11-04: 只返回未忽略的预警
   */
  fetchChangeAlerts: async (dealId: string) => {
    set({ isChangeAlertsLoading: true, error: null })
    try {
      const alerts = await changeService.getChangeAlerts(dealId)
      set({
        changeAlerts: alerts,
        isChangeAlertsLoading: false,
      })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : '获取变更预警失败',
        isChangeAlertsLoading: false,
      })
    }
  },
  
  /**
   * 忽略变更预警
   * BR-T11-07: dismissChangeAlert 后从列表移除
   */
  dismissChangeAlert: async (alertId: string, reason?: string) => {
    try {
      await changeService.dismissAlert(alertId, reason)
      // 从列表中移除已忽略的预警
      set((state) => ({
        changeAlerts: state.changeAlerts.filter((a) => a.id !== alertId),
      }))
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : '忽略预警失败',
      })
    }
  },
}))

